import axios from 'axios';

// Relative '' base -> requests hit /api/* through Vite's dev proxy (see
// vite.config.ts) in development, and through nginx's reverse proxy in
// production. Set VITE_API_BASE_URL only if the API is served from a
// different origin.
export const http = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  headers: { 'Content-Type': 'application/json' },
  // Every /api/* route (other than /api/auth/*) now requires the
  // better-auth session cookie — axios doesn't send cookies on
  // cross-origin-shaped requests without this even when same-origin via
  // the dev proxy's rewritten Origin header.
  withCredentials: true,
});

// Every endpoint this client talks to returns JSON. If /api/* comes back as
// an HTML document, the proxy target is not the EMR API (e.g. another app is
// squatting on the port and its SPA fallback answers every path with a 200
// index.html). Reject so callers hit their existing "server not reachable"
// error paths instead of consuming an HTML string as data.
let redirectingToLogin = false;

http.interceptors.response.use(
  (res) => {
    const contentType = String(res.headers?.['content-type'] ?? '');
    if (contentType.includes('text/html')) {
      return Promise.reject(
        new Error(`Expected JSON from ${res.config?.url} but received HTML — is the EMR API running?`),
      );
    }
    return res;
  },
  async (err) => {
    // Session missing/expired — bounce to login instead of surfacing a raw
    // 401 through each call site's own error handling. Skip this on the
    // login page itself so a failed sign-in attempt doesn't self-redirect,
    // and skip /api/auth/* so a single racy 401 right after sign-in (a
    // request firing before the new session cookie is fully committed)
    // can't hard-reload the app before the session even settles.
    const url = String(err?.config?.url ?? '');
    const isAuthRoute = url.startsWith('/api/auth/');
    if (err?.response?.status === 401 && window.location.pathname !== '/login' && !isAuthRoute) {
      // The first authenticated request after sign-in — or after any of
      // this app's hard window.location navigations between pages, which
      // fully reboot the SPA and re-run this race every time — can lose a
      // timing race against the browser committing the new session
      // cookie. Retry with backoff instead of treating the first 401 as a
      // real "logged out"; only redirect once retries are exhausted.
      const retryCount = err.config?._retryCount ?? 0;
      const maxRetries = 3;
      const delaysMs = [300, 600, 1000];
      if (retryCount < maxRetries) {
        await new Promise((resolve) => setTimeout(resolve, delaysMs[retryCount]));
        return http({ ...err.config, _retryCount: retryCount + 1 });
      }
      // The redirectingToLogin guard collapses a burst of concurrent
      // still-failing 401s into a single reload instead of each one
      // re-triggering navigation.
      if (!redirectingToLogin) {
        redirectingToLogin = true;
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  },
);
